/**
 * Travel Carbon Estimator
 * Copyright (C) 2026  Manuel Leduc
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import { createApp } from 'vue'
import { createPinia } from 'pinia'
import 'vuetify/styles'
import { createVuetify } from 'vuetify'
import { aliases, mdi } from 'vuetify/iconsets/mdi-svg'
import '@fontsource/roboto/100.css'
import '@fontsource/roboto/300.css'
import '@fontsource/roboto/400.css'
import '@fontsource/roboto/500.css'
import '@fontsource/roboto/700.css'
import '@fontsource/roboto/900.css'
import '@fontsource/roboto/100-italic.css'
import '@fontsource/roboto/300-italic.css'
import '@fontsource/roboto/400-italic.css'
import '@fontsource/roboto/500-italic.css'
import '@fontsource/roboto/700-italic.css'
import '@fontsource/roboto/900-italic.css'

import App from './App.vue'
import router from './router'
import { i18n } from './lib/i18n'
import { detectLocale, getSavedLocale, setLocale } from './composables/useLocale'

const vuetify = createVuetify({
  icons: {
    defaultSet: 'mdi',
    aliases,
    sets: { mdi },
  },
  theme: {
    defaultTheme: 'tceLight',
    themes: {
      tceLight: {
        dark: false,
        colors: {
          primary: '#1B5E3A',
          secondary: '#0288A6',
          accent: '#C2924A',
          background: '#F5F7F4',
          surface: '#FFFFFF',
          error: '#B3261E',
          info: '#0277BD',
          success: '#2E7D32',
          warning: '#ED6C02',
        },
      },
      tceDark: {
        dark: true,
        colors: {
          primary: '#6FBF8F',
          secondary: '#4FC3DC',
          accent: '#E0B673',
          background: '#121512',
          surface: '#1B1F1C',
          error: '#F2B8B5',
          info: '#7EC8E3',
          success: '#7DD49A',
          warning: '#F4A66A',
        },
      },
    },
  },
})

const app = createApp(App)

app.use(vuetify)
app.use(createPinia())
app.use(router)
app.use(i18n)

// Resolve locale before mount: use saved locale or detect from browser
const initialLocale = getSavedLocale() ?? detectLocale()
setLocale(initialLocale).then(() => app.mount('#app'))
